import type {
  ExaminationResponse,
  GradingRequest,
  GradingResponse,
} from '../../../domain/precon-supporter/types/examination';

/**
 * Precon Supporter API client
 */
export class PreconSupporterApiClient {
  private readonly baseUrl = this.getBaseUrl();
  private readonly apiKey = process.env.GATSBY_EXAMINATION_API_KEY;

  /**
   * 環境変数からベースURLを取得
   * @returns ベースURL
   */
  private getBaseUrl(): string {
    const endpoint = process.env.GATSBY_EXAMINATION_API_ENDPOINT;

    if (!endpoint) {
      throw new Error(
        'GATSBY_EXAMINATION_API_ENDPOINT環境変数が設定されていません。' +
          '.env.developmentまたは.env.stagingまたは.env.productionファイルで設定してください。'
      );
    }

    return endpoint;
  }

  /**
   * 試験データを取得
   * @param params - 取得パラメータ
   * @returns 試験データ
   */
  async fetchExamination(params: {
    courseId: 'basic' | 'advanced';
    examinationId: number;
  }): Promise<ExaminationResponse> {
    const timestamp = Date.now();
    const response = await fetch(
      `${this.baseUrl}/examination?course_id=${params.courseId}&examination_id=${params.examinationId}&timestamp=${timestamp}`,
      {
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(
        `試験データの取得に失敗しました: HTTP ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * 採点を実行
   * @param request - 採点リクエスト
   * @returns 採点結果
   */
  async submitGrading(request: GradingRequest): Promise<GradingResponse> {
    const response = await fetch(`${this.baseUrl}/scores`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`採点の実行に失敗しました: HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * 受講者（passer）を作成する
   * @param request - 登録データ
   * @param passerToken - 認証トークン
   * @returns レスポンス
   */
  async createPasser(
    request: Record<string, unknown>,
    passerToken: string
  ): Promise<any> {
    const response = await fetch(`${this.baseUrl}/passers`, {
      method: 'POST',
      headers: this.getHeaders(passerToken),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 403 || response.status === 401) {
        throw new Error('AUTHORIZATION_EXPIRED');
      }
      throw new Error(`受講者登録に失敗しました: HTTP ${response.status}`);
    }

    // try to parse JSON, but return text if not JSON
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return response.json();
    }
    return { message: await response.text() };
  }

  /**
   * APIリクエスト用のヘッダーを取得
   * @param passerToken - 認証トークン（オプション）
   * @returns ヘッダー
   */
  private getHeaders(passerToken?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // API Key が設定されている場合のみ追加（開発環境）
    if (this.apiKey) {
      headers['x-api-key'] = this.apiKey;
    }

    // passerToken が提供された場合、Authorization ヘッダーに設定
    if (passerToken) {
      headers['Authorization'] = `Bearer ${passerToken}`;
    }

    return headers;
  }
}
